import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import { Building2, Calendar, DollarSign, Package, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CenterDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [packagesDialog, setPackagesDialog] = useState(null);
  const [editingPackages, setEditingPackages] = useState([]);
  const [savingPackages, setSavingPackages] = useState(false);
  const [showAddCenter, setShowAddCenter] = useState(false);
  const [newCenter, setNewCenter] = useState({
    name: '', description: '', location: '', address: '', contact: '',
    facilities: '', treatments_offered: '', image_url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874',
  });
  const [addingCenter, setAddingCenter] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/center/dashboard`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const openPackagesDialog = (center) => {
    setPackagesDialog(center);
    setEditingPackages(center.corporate_packages?.length ? [...center.corporate_packages] : []);
  };

  const addPackageRow = () => {
    setEditingPackages([...editingPackages, { id: `new-${Date.now()}`, name: '', description: '', price: 0, features: [] }]);
  };

  const updatePackage = (index, field, value) => {
    const next = [...editingPackages];
    next[index] = { ...next[index], [field]: value };
    if (field === 'features') next[index].features = value.split(',').map(s => s.trim()).filter(Boolean);
    setEditingPackages(next);
  };

  const removePackage = (index) => {
    setEditingPackages(editingPackages.filter((_, i) => i !== index));
  };

  const handleAddCenter = async (e) => {
    e.preventDefault();
    setAddingCenter(true);
    try {
      await axios.post(
        `${API}/wellness-centers`,
        {
          name: newCenter.name,
          description: newCenter.description,
          location: newCenter.location,
          address: newCenter.address,
          contact: newCenter.contact,
          facilities: newCenter.facilities.split(',').map(s => s.trim()).filter(Boolean),
          treatments_offered: newCenter.treatments_offered.split(',').map(s => s.trim()).filter(Boolean),
          image_url: newCenter.image_url || 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874',
          rating: 0,
          google_rating: null,
        },
        { headers: getAuthHeader() }
      );
      toast.success('Center created');
      setShowAddCenter(false);
      setNewCenter({ name: '', description: '', location: '', address: '', contact: '', facilities: '', treatments_offered: '', image_url: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create center');
    } finally {
      setAddingCenter(false);
    }
  };

  const saveCorporatePackages = async () => {
    if (!packagesDialog) return;
    setSavingPackages(true);
    try {
      const packages = editingPackages.map(p => ({
        id: p.id?.startsWith('new-') ? `pkg-${Date.now()}-${Math.random().toString(36).slice(2)}` : p.id,
        name: p.name || 'Package',
        description: p.description || '',
        price: Number(p.price) || 0,
        features: Array.isArray(p.features) ? p.features : (p.features || '').split(',').map(s => s.trim()).filter(Boolean),
      }));
      await axios.patch(
        `${API}/center/centers/${packagesDialog.id}`,
        { corporate_packages: packages },
        { headers: getAuthHeader() }
      );
      toast.success('Corporate packages updated');
      setPackagesDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingPackages(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container mx-auto px-6 lg:px-12 py-12" data-testid="center-dashboard">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Wellness Center Dashboard
          </h1>
          <p className="text-lg text-gray-600">Manage your centers, bookings, and corporate packages</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary" />
                My Centers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{data?.centers?.length ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{data?.total_bookings ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                Total Commission (80%)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">₹{(data?.total_commission ?? 0).toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>

        {/* My Centers + Corporate packages */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>My Centers</CardTitle>
              <Button onClick={() => setShowAddCenter(true)} data-testid="add-center-btn">
                <Plus className="h-4 w-4 mr-2" /> Add center
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {data?.centers?.length > 0 ? (
              <div className="space-y-6">
                {data.centers.map((center) => (
                  <div key={center.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{center.name}</h3>
                        <p className="text-sm text-gray-600">{center.location} · {center.contact}</p>
                        <p className="text-sm text-primary mt-1">Rating: {center.rating} · Google: {center.google_rating ?? '—'}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => openPackagesDialog(center)}>
                        <Package className="h-4 w-4 mr-1" /> Corporate packages
                      </Button>
                    </div>
                    {center.corporate_packages?.length > 0 && (
                      <ul className="mt-3 text-sm text-gray-600 space-y-1">
                        {center.corporate_packages.map((p) => (
                          <li key={p.id}>{p.name} — ₹{p.price}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-6">No centers yet. Click &quot;Add center&quot; to create one.</p>
            )}
          </CardContent>
        </Card>

        {/* Bookings */}
        {data?.bookings?.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Recent Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {data.bookings.slice(0, 15).map((b) => (
                  <div key={b.id} className="flex justify-between items-center border-b pb-3 last:border-0">
                    <div>
                      <span className="font-medium">{b.treatment_name}</span>
                      <span className="text-gray-600 ml-2">@ {b.center_name}</span>
                    </div>
                    <div className="text-sm">
                      <span>{b.start_date}</span>
                      <span className="ml-2 font-semibold text-primary">₹{b.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {(!data?.centers?.length && !data?.bookings?.length) && (
          <Card className="text-center py-12">
            <CardContent>
              <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No centers or bookings yet.</p>
              <Button onClick={() => setShowAddCenter(true)}><Plus className="h-4 w-4 mr-2" /> Add your first center</Button>
            </CardContent>
          </Card>
        )}
      </main>

      <Footer />

      {/* Add center dialog */}
      <Dialog open={showAddCenter} onOpenChange={setShowAddCenter}>
        <DialogContent className="max-w-lg" data-testid="add-center-dialog">
          <DialogHeader>
            <DialogTitle>Add wellness center</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCenter} className="space-y-4">
            <div>
              <Label htmlFor="center-name">Name</Label>
              <Input id="center-name" required value={newCenter.name} onChange={(e) => setNewCenter({ ...newCenter, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="center-desc">Description</Label>
              <Input id="center-desc" value={newCenter.description} onChange={(e) => setNewCenter({ ...newCenter, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="center-location">Location</Label>
                <Input id="center-location" required value={newCenter.location} onChange={(e) => setNewCenter({ ...newCenter, location: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="center-contact">Contact</Label>
                <Input id="center-contact" required value={newCenter.contact} onChange={(e) => setNewCenter({ ...newCenter, contact: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="center-address">Address</Label>
              <Input id="center-address" required value={newCenter.address} onChange={(e) => setNewCenter({ ...newCenter, address: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="center-facilities">Facilities (comma-separated)</Label>
              <Input id="center-facilities" placeholder="Parking, AC, etc." value={newCenter.facilities} onChange={(e) => setNewCenter({ ...newCenter, facilities: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="center-treatments">Treatments offered (comma-separated)</Label>
              <Input id="center-treatments" placeholder="Abhyanga, Shirodhara, etc." value={newCenter.treatments_offered} onChange={(e) => setNewCenter({ ...newCenter, treatments_offered: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAddCenter(false)}>Cancel</Button>
              <Button type="submit" disabled={addingCenter}>{addingCenter ? 'Creating...' : 'Create center'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Corporate packages dialog */}
      <Dialog open={!!packagesDialog} onOpenChange={(open) => !open && setPackagesDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="corporate-packages-dialog">
          <DialogHeader>
            <DialogTitle>Corporate wellness packages — {packagesDialog?.name}</DialogTitle>
          </DialogHeader>
          {packagesDialog && (
            <div className="space-y-4">
              <Button type="button" variant="outline" size="sm" onClick={addPackageRow}>
                <Plus className="h-4 w-4 mr-2" /> Add package
              </Button>
              {editingPackages.map((pkg, index) => (
                <div key={pkg.id || index} className="border rounded p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Package name"
                      value={pkg.name || ''}
                      onChange={(e) => updatePackage(index, 'name', e.target.value)}
                    />
                    <Input
                      type="number"
                      placeholder="Price (₹)"
                      value={pkg.price ?? ''}
                      onChange={(e) => updatePackage(index, 'price', e.target.value)}
                    />
                  </div>
                  <Input
                    placeholder="Description"
                    value={pkg.description || ''}
                    onChange={(e) => updatePackage(index, 'description', e.target.value)}
                  />
                  <Input
                    placeholder="Features (comma-separated)"
                    value={Array.isArray(pkg.features) ? pkg.features.join(', ') : (pkg.features || '')}
                    onChange={(e) => updatePackage(index, 'features', e.target.value)}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removePackage(index)}>Remove</Button>
                </div>
              ))}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPackagesDialog(null)}>Cancel</Button>
                <Button onClick={saveCorporatePackages} disabled={savingPackages}>{savingPackages ? 'Saving...' : 'Save'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CenterDashboard;
